import React, { useState, useEffect } from 'react';
import { Plus, PencilSimple, Trash, Calendar, Warning, CheckCircle } from '@phosphor-icons/react';
import axios from '../../utils/axiosInstance';
import { vehicleLabel } from '../../utils/vehicleLabel';
import { WlasnoscFilter, WlasnoscBadge, normalizeWlasnosc } from '../shared/WlasnoscFilter';

const API = `${window.location.origin}/api`;

// Calculate days until date
const getDaysUntil = (dateString) => {
  if (!dateString) return null;
  const target = new Date(dateString);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  target.setHours(0, 0, 0, 0);
  const diffTime = target - today;
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  return diffDays;
};

// Get status color based on days remaining
const getStatusColor = (dateString) => {
  const days = getDaysUntil(dateString);
  if (days === null) return 'bg-gray-100 text-gray-700';
  if (days < 0) return 'bg-red-100 text-red-800 border-red-300';
  if (days <= 30) return 'bg-yellow-100 text-yellow-900 border-yellow-300';
  return 'bg-green-100 text-green-800 border-green-300';
};

// Get status badge
const getStatusBadge = (dateString) => {
  const days = getDaysUntil(dateString);
  if (days === null) return { text: 'Brak daty', icon: null };
  if (days < 0) return { text: `Przeterminowane (${Math.abs(days)} dni)`, icon: <Warning className="w-4 h-4" weight="bold" /> };
  if (days <= 30) return { text: `Pozostało ${days} dni`, icon: <Warning className="w-4 h-4" /> };
  return { text: `Ważne (${days} dni)`, icon: <CheckCircle className="w-4 h-4" /> };
};

export default function TerminyTab() {
  const [terminy, setTerminy] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [pojazdy, setPojazdy] = useState([]);  // Load from pojazdy collection
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [wlasnoscFilter, setWlasnoscFilter] = useState('all');

  // pojazd_id → wlasnosc map for row-level filter/badge
  const pojazdWlasnoscById = React.useMemo(() => {
    const m = {};
    for (const p of pojazdy) m[p.id] = normalizeWlasnosc(p.wlasnosc);
    return m;
  }, [pojazdy]);

  const filteredTerminy = React.useMemo(() => {
    if (wlasnoscFilter === 'all') return terminy;
    return terminy.filter((t) => (pojazdWlasnoscById[t.pojazd_id] || 'firmowy') === wlasnoscFilter);
  }, [terminy, wlasnoscFilter, pojazdWlasnoscById]);

  const wlasnoscCounts = React.useMemo(() => {
    const c = { all: terminy.length, firmowy: 0, prywatny: 0 };
    for (const t of terminy) c[pojazdWlasnoscById[t.pojazd_id] || 'firmowy']++;
    return c;
  }, [terminy, pojazdWlasnoscById]);
  
  // Form state
  const [formData, setFormData] = useState({
    pojazd_id: '',  // Changed from nazwa_pojazdu
    ubezpieczenie: '',
    przeglad: '',
    employee_id: ''
  });

  useEffect(() => {
    fetchTerminy();
    fetchEmployees();
    fetchPojazdy();
  }, []);

  const fetchPojazdy = async () => {
    try {
      const response = await axios.get(`${API}/pojazdy`);
      setPojazdy(response.data);
    } catch (err) {
      console.error('Error fetching pojazdy:', err);
    }
  };

  const fetchEmployees = async () => {
    try {
      const response = await axios.get(`${API}/employees`);
      setEmployees(response.data);
    } catch (err) {
      console.error('Error fetching employees:', err);
    }
  };

  const fetchTerminy = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/terminy`);
      setTerminy(response.data);
    } catch (err) {
      console.error('Error fetching terminy:', err);
      alert('Błąd podczas pobierania danych');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      if (editingId) {
        // Update existing
        await axios.put(`${API}/terminy/${editingId}`, formData);
      } else {
        // Create new
        await axios.post(`${API}/terminy`, formData);
      }
      
      // Reset form
      setFormData({ pojazd_id: '', ubezpieczenie: '', przeglad: '', employee_id: '' });
      setShowForm(false);
      setEditingId(null);
      fetchTerminy();
    } catch (err) {
      console.error('Error saving termin:', err);
      const errorMsg = err.response?.data?.detail || 'Błąd podczas zapisywania';
      alert(errorMsg);
    }
  };

  const handleEdit = (termin) => {
    setFormData({
      pojazd_id: termin.pojazd_id || termin.id,  // Use pojazd_id if exists, else termin.id (from migration)
      ubezpieczenie: termin.ubezpieczenie || '',
      przeglad: termin.przeglad || '',
      employee_id: termin.employee_id || ''
    });
    setEditingId(termin.id);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Czy na pewno chcesz usunąć ten wpis?')) return;
    
    try {
      await axios.delete(`${API}/terminy/${id}`);
      fetchTerminy();
    } catch (err) {
      console.error('Error deleting termin:', err);
      alert('Błąd podczas usuwania');
    }
  };

  const handleCancel = () => {
    setFormData({ pojazd_id: '', ubezpieczenie: '', przeglad: '', employee_id: '' });
    setShowForm(false);
    setEditingId(null);
  };

  if (loading) {
    return <div className="text-center py-12">Ładowanie...</div>;
  }

  return (
    <div className="max-w-[1920px] mx-auto px-6 py-6 space-y-6">
      <div className="flex items-center justify-between mb-6 gap-4 flex-wrap">
        <div>
          <h2 className="text-3xl font-bold text-slate-800">Terminy</h2>
          <p className="text-slate-600 mt-1">Zarządzanie terminami ubezpieczeń i przeglądów</p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <WlasnoscFilter
            value={wlasnoscFilter}
            onChange={setWlasnoscFilter}
            counts={wlasnoscCounts}
          />
          <button
            onClick={() => setShowForm(!showForm)}
            className="px-5 py-3 bg-muted hover:from-blue-700 hover:to-blue-800 text-white rounded-xl font-semibold flex items-center gap-2 shadow-lg hover:border border-border transition-all"
          >
            <Plus className="w-5 h-5" weight="bold" />
            Dodaj pojazd
          </button>
        </div>
      </div>

      {/* Add/Edit Form */}
      {showForm && (
        <div className="bg-muted rounded-sm p-6 shadow-lg border border-border">
          <h3 className="text-xl font-bold text-slate-800 mb-4">
            {editingId ? 'Edytuj pojazd' : 'Dodaj nowy pojazd'}
          </h3>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">
                  Pojazd *
                </label>
                <select
                  value={formData.pojazd_id}
                  onChange={(e) => setFormData({ ...formData, pojazd_id: e.target.value })}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">Wybierz pojazd</option>
                  {pojazdy.map((p) => (
                    <option key={p.id} value={p.id}>
                      {vehicleLabel(p)}
                    </option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">
                  Ubezpieczenie (data ważności)
                </label>
                <input
                  type="date"
                  value={formData.ubezpieczenie}
                  onChange={(e) => setFormData({ ...formData, ubezpieczenie: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">
                  Przegląd (data ważności)
                </label>
                <input
                  type="date"
                  value={formData.przeglad}
                  onChange={(e) => setFormData({ ...formData, przeglad: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">
                  Przypisany pracownik
                </label>
                <select
                  value={formData.employee_id}
                  onChange={(e) => setFormData({ ...formData, employee_id: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">Brak przypisania</option>
                  {employees.map((emp) => (
                    <option key={emp._id} value={emp._id}>
                      {emp.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            
            <div className="flex gap-3 pt-2">
              <button
                type="submit"
                className="px-6 py-3 bg-foreground hover:bg-black text-white rounded-lg font-semibold transition-colors"
              >
                {editingId ? 'Zapisz zmiany' : 'Dodaj pojazd'}
              </button>
              <button
                type="button"
                onClick={handleCancel}
                className="px-6 py-3 bg-gray-300 hover:bg-gray-400 text-gray-700 rounded-lg font-semibold transition-colors"
              >
                Anuluj
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Terminy Table */}
      <div className="bg-white rounded-sm shadow-lg border border-gray-100 overflow-hidden">
        {filteredTerminy.length === 0 ? (
          <div className="text-center py-16">
            <Calendar className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500 text-lg">
              {terminy.length === 0
                ? 'Brak wpisów. Dodaj pierwszy pojazd!'
                : 'Brak wpisów w wybranym filtrze.'}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-muted">
                <tr>
                  <th className="text-left py-4 px-6 text-xs font-bold uppercase tracking-wider text-slate-600">
                    Nazwa pojazdu
                  </th>
                  <th className="text-left py-4 px-6 text-xs font-bold uppercase tracking-wider text-slate-600">
                    Ubezpieczenie
                  </th>
                  <th className="text-left py-4 px-6 text-xs font-bold uppercase tracking-wider text-slate-600">
                    Przegląd
                  </th>
                  <th className="text-right py-4 px-6 text-xs font-bold uppercase tracking-wider text-slate-600">
                    Akcje
                  </th>
                </tr>
              </thead>
              <tbody>
                {filteredTerminy.map((termin, idx) => (
                  <tr
                    key={termin.id}
                    className={`hover:bg-muted transition-colors duration-200 ${
                      idx % 2 === 0 ? 'bg-white' : 'bg-slate-50/40'
                    }`}
                  >
                    <td className="py-4 px-6 border-b border-gray-100">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-foreground rounded-lg flex items-center justify-center">
                          <Calendar className="w-6 h-6 text-white" weight="bold" />
                        </div>
                        <div className="flex flex-col gap-1">
                          <span className="font-semibold text-slate-800 text-lg">
                            {vehicleLabel(termin)}
                          </span>
                          <WlasnoscBadge value={pojazdWlasnoscById[termin.pojazd_id]} size="xs" />
                        </div>
                      </div>
                    </td>
                    
                    <td className="py-4 px-6 border-b border-gray-100">
                      {termin.ubezpieczenie ? (
                        <div className="space-y-1">
                          <div className="text-sm font-medium text-slate-700">
                            {new Date(termin.ubezpieczenie).toLocaleDateString('pl-PL')}
                          </div>
                          <div className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold border ${getStatusColor(termin.ubezpieczenie)}`}>
                            {getStatusBadge(termin.ubezpieczenie).icon}
                            {getStatusBadge(termin.ubezpieczenie).text}
                          </div>
                        </div>
                      ) : (
                        <span className="text-gray-400 text-sm">Brak daty</span>
                      )}
                    </td>
                    
                    <td className="py-4 px-6 border-b border-gray-100">
                      {termin.przeglad ? (
                        <div className="space-y-1">
                          <div className="text-sm font-medium text-slate-700">
                            {new Date(termin.przeglad).toLocaleDateString('pl-PL')}
                          </div>
                          <div className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold border ${getStatusColor(termin.przeglad)}`}>
                            {getStatusBadge(termin.przeglad).icon}
                            {getStatusBadge(termin.przeglad).text}
                          </div>
                        </div>
                      ) : (
                        <span className="text-gray-400 text-sm">Brak daty</span>
                      )}
                    </td>
                    
                    <td className="py-4 px-6 border-b border-gray-100 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => handleEdit(termin)}
                          className="p-2 hover:bg-muted rounded-lg transition-colors text-foreground"
                          title="Edytuj"
                        >
                          <PencilSimple className="w-5 h-5" weight="bold" />
                        </button>
                        <button
                          onClick={() => handleDelete(termin.id)}
                          className="p-2 hover:bg-red-100 rounded-lg transition-colors text-red-600"
                          title="Usuń"
                        >
                          <Trash className="w-5 h-5" weight="bold" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Summary Cards */}
      {terminy.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-muted rounded-sm p-6 text-white shadow-lg">
            <div className="flex items-center gap-3 mb-2">
              <CheckCircle className="w-8 h-8" weight="bold" />
              <h3 className="text-lg font-semibold">Aktualne</h3>
            </div>
            <p className="text-3xl font-bold">
              {terminy.filter(t => {
                const ub = getDaysUntil(t.ubezpieczenie);
                const pr = getDaysUntil(t.przeglad);
                return (ub && ub > 30) || (pr && pr > 30);
              }).length}
            </p>
          </div>
          
          <div className="bg-muted rounded-sm p-6 text-white shadow-lg">
            <div className="flex items-center gap-3 mb-2">
              <Warning className="w-8 h-8" weight="bold" />
              <h3 className="text-lg font-semibold">Wkrótce wygasają</h3>
            </div>
            <p className="text-3xl font-bold">
              {terminy.filter(t => {
                const ub = getDaysUntil(t.ubezpieczenie);
                const pr = getDaysUntil(t.przeglad);
                return (ub && ub > 0 && ub <= 30) || (pr && pr > 0 && pr <= 30);
              }).length}
            </p>
          </div>
          
          <div className="bg-signal rounded-sm p-6 text-white shadow-lg">
            <div className="flex items-center gap-3 mb-2">
              <Warning className="w-8 h-8" weight="bold" />
              <h3 className="text-lg font-semibold">Przeterminowane</h3>
            </div>
            <p className="text-3xl font-bold">
              {terminy.filter(t => {
                const ub = getDaysUntil(t.ubezpieczenie);
                const pr = getDaysUntil(t.przeglad);
                return (ub && ub < 0) || (pr && pr < 0);
              }).length}
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
